import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { recipes } from '../data/recipes';
import { useSavedRituals } from '../context/SavedRitualsContext';
import { BookOpen, Clock, ArrowRight, Bookmark } from 'lucide-react';

const Recipes = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const navigate = useNavigate();
  const { isSaved, toggleSaved, savedCount } = useSavedRituals();

  return (
    <div>
      {/* Hero */}
      <section className="bg-gradient-to-b from-[#efe9db] to-[#faf7f1]">
        <div className="max-w-5xl mx-auto px-6 py-20 md:py-28 text-center">
          <div className="inline-flex items-center gap-2 uppercase tracking-[0.35em] text-xs text-[#7a8b6b] mb-4">
            <BookOpen className="w-4 h-4" /> Recipe Journal
          </div>
          <h1 className="font-serif-elegant text-5xl md:text-7xl mb-4">Simple Herbal Rituals</h1>
          <p className="text-[#4a4640] max-w-2xl mx-auto leading-relaxed">
            Small, embodied recipes for using your herbs. Each ritual pairs a Herbs We Love
            remedy with a moment of care &mdash; because how you use the medicine matters as much as the medicine itself.
          </p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
        {savedCount > 0 && (
          <div className="flex justify-end mb-6">
            <Link to="/rituals" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#3a4a3b] link-underline">
              <Bookmark className="w-4 h-4" /> View my {savedCount} saved ritual{savedCount > 1 ? 's' : ''}
            </Link>
          </div>
        )}
        <div className="grid md:grid-cols-2 gap-x-8 gap-y-12">
          {recipes.map(r => {
            const saved = isSaved(r.id);
            return (
              <div key={r.id} className="group relative">
                <button
                  onClick={() => toggleSaved(r.id)}
                  aria-label={saved ? 'Remove from saved rituals' : 'Save ritual'}
                  className={`absolute top-3 right-3 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-[#faf7f1]/90 border border-[#c9c1ad] transition-colors ${saved ? 'text-[#3a4a3b]' : 'text-[#2f2a24] hover:text-[#3a4a3b]'}`}
                >
                  <Bookmark className={`w-4 h-4 ${saved ? 'fill-current' : ''}`} />
                </button>
                <button
                  onClick={() => navigate(`/recipes/${r.id}`)}
                  className="w-full text-left"
                >
                  <div className="relative aspect-[5/4] overflow-hidden bg-[#efe9db] mb-5">
                    <img
                      src={r.image}
                      alt={r.title}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                    />
                    <div className="absolute top-3 left-3 badge-sale">{r.moment}</div>
                  </div>
                  <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-[#7a8b6b] mb-2">
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {r.time}</span>
                    <span>&middot;</span>
                    <span>{r.difficulty}</span>
                  </div>
                  <h3 className="font-serif-elegant text-3xl mb-2 group-hover:text-[#3a4a3b] transition-colors">{r.title}</h3>
                  <p className="text-sm text-[#7a8b6b] mb-3">{r.subtitle}</p>
                  <p className="text-[#4a4640] leading-relaxed text-sm mb-4">{r.intro}</p>
                  <span className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-[#3a4a3b] group-hover:gap-2 transition-all">
                    Read the ritual <ArrowRight className="w-3 h-3" />
                  </span>
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <section className="max-w-4xl mx-auto px-6 py-16 text-center">
        <h2 className="font-serif-elegant text-3xl md:text-4xl mb-4">Have a ritual to share?</h2>
        <p className="text-[#4a4640] mb-6">We love hearing how you weave herbs into your day. Send us your favorite ritual and we may feature it.</p>
        <Link to="/about" className="btn-primary">Share Yours</Link>
      </section>
    </div>
  );
};

export default Recipes;
